import { toPng, toBlob } from 'html-to-image';

export type ExportStyle = 'styled' | 'transparent';

interface CaptureOptions {
  /** 'styled' keeps the element background; 'transparent' strips it */
  style?: ExportStyle;
  /** Scale factor for higher resolution (default 3 ≈ 4K for typical dashboard widths) */
  scale?: number;
}

/** Shared html-to-image options for all capture methods. */
function buildOptions(options: CaptureOptions) {
  const { style = 'styled', scale = 3 } = options;
  return {
    pixelRatio: scale,
    backgroundColor: style === 'transparent' ? 'transparent' : undefined,
    skipFonts: true, // fonts are already loaded in the browser — avoids CORS errors with Google Fonts
    cacheBust: true,
    // Skip the floating export toolbar so it doesn't appear in the capture
    filter: (node: HTMLElement) => {
      return !node?.dataset?.overlayToolbar;
    },
  };
}

/**
 * Capture a DOM element as a PNG data URL using html-to-image.
 * Uses the browser's native rendering (foreignObject), so it supports
 * all modern CSS including oklch(), color-mix(), etc.
 */
export async function captureElementAsPngDataUrl(
  element: HTMLElement,
  options: CaptureOptions = {}
): Promise<string> {
  return toPng(element, buildOptions(options));
}

/**
 * Capture a DOM element as a Blob.
 */
export async function captureElementAsBlob(
  element: HTMLElement,
  options: CaptureOptions = {}
): Promise<Blob> {
  const blob = await toBlob(element, buildOptions(options));
  if (!blob) throw new Error('Failed to create blob from element');
  return blob;
}

/**
 * Download a data URL as a PNG file.
 */
export function downloadPng(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Copy an element's image to the clipboard.
 * Returns true on success, false on failure.
 */
export async function copyElementToClipboard(
  element: HTMLElement,
  options: CaptureOptions = {}
): Promise<boolean> {
  try {
    const blob = await captureElementAsBlob(element, options);
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    return true;
  } catch (err) {
    console.error('Failed to copy image to clipboard:', err);
    return false;
  }
}

/**
 * Save element image to device — uses Web Share API on mobile, falls back to download.
 */
export async function saveElementToDevice(
  element: HTMLElement,
  filename: string,
  options: CaptureOptions = {}
): Promise<boolean> {
  try {
    if (navigator.share && navigator.canShare) {
      const blob = await captureElementAsBlob(element, options);
      const file = new File([blob], filename, { type: 'image/png' });

      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: filename });
          return true;
        } catch (shareError) {
          if (shareError instanceof Error && shareError.name !== 'AbortError') {
            console.warn('Web Share API failed, falling back to download:', shareError);
          }
        }
      }
    }

    // Fallback: download
    const dataUrl = await captureElementAsPngDataUrl(element, options);
    downloadPng(dataUrl, filename);
    return true;
  } catch (err) {
    console.error('Failed to save image to device:', err);
    return false;
  }
}

/**
 * Generate a filename for an exported overlay.
 */
export function generateOverlayFilename(
  metricName: string,
  athleteName?: string
): string {
  const parts = ['cgm'];
  if (athleteName) parts.push(athleteName.replace(/[^a-z0-9]/gi, '_').toLowerCase());
  parts.push(metricName.replace(/[^a-z0-9]/gi, '_').toLowerCase());
  parts.push(String(Date.now()));
  return parts.join('_') + '.png';
}
